import React, { useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Button,
  EmptyState,
  IconPlus,
  IconUsers,
  ListPage,
  Pagination,
  SectionTitleRow,
  Table,
  TableSkeletonRows,
  Tag,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../../ui/v2';
import { api, apiEndpoints } from '../../utils/apiService';
import { type TableSortState, toggleSortState } from '../../utils/listOrdering';
import { TableSearch } from './TableSearch';
import {
  CreateUserDialog,
  EditStoreGroupUserDialog,
  EditUserDialog,
  type IntakePickerControls,
  IntakePrefillDialog,
} from './UserDialogs';
import { filterAndSortRows, rowSearchText } from './clientTable';
import {
  EMPTY_STORE_GROUP_USER_FORM,
  EMPTY_USER_FORM,
  responseError,
  type SelectOption,
  storeGroupRoleLabel,
  type StoreGroupUser,
  type StoreGroupUserFormData,
  storeGroupUserFormFrom,
  storeGroupUserCreatedMessage,
  type StoreGroupUserMode,
  storeGroupUserPayload,
  type StoreUser,
  storeUserCreatedMessage,
  storeUserName,
  storeUserRoleTone,
  storeUserStoreLabel,
  thrownErrorMessage,
  updateUserForm,
  type UserAssignmentType,
  type UserFormData,
  validateEditStoreGroupUserForm,
  validateStoreGroupUserForm,
} from './usersModel';

type Column = {
  key: string;
  label: string;
  width?: number;
  sortKey?: string;
};

const STORE_USER_COLUMNS: Column[] = [
  { key: 'id', label: 'ID', width: 80, sortKey: 'id' },
  { key: 'name', label: 'Name', sortKey: 'name' },
  { key: 'email', label: 'Email', sortKey: 'email' },
  { key: 'role', label: 'Role', width: 120, sortKey: 'role' },
  { key: 'store', label: 'Store', sortKey: 'store_name' },
  { key: 'actions', label: 'Actions', width: 128 },
];

type GroupUserSortKey = 'id' | 'user' | 'store_group' | 'role';

const STORE_GROUP_USER_COLUMNS: Column[] = [
  { key: 'id', label: 'ID', width: 80, sortKey: 'id' },
  { key: 'user', label: 'User', sortKey: 'user' },
  { key: 'store_group', label: 'Store Group', sortKey: 'store_group' },
  { key: 'role', label: 'Role', width: 120, sortKey: 'role' },
  { key: 'actions', label: 'Actions', width: 160 },
];

const Placeholder: React.FC<{ value?: React.ReactNode }> = ({ value }) =>
  value ? <>{value}</> : <span className="text-v2-fg-3">—</span>;

const Header: React.FC<{
  columns: Column[];
  sort?: TableSortState<string>;
  onSort?: (key: string) => void;
  loading?: boolean;
}> = ({ columns, sort, onSort, loading }) => (
  <THead>
    <tr>
      {columns.map((column) => (
        <TH
          key={column.key}
          width={column.width}
          sortable={Boolean(column.sortKey) && !loading}
          active={Boolean(column.sortKey) && sort?.key === column.sortKey}
          dir={sort?.direction}
          onSort={() => column.sortKey && onSort?.(column.sortKey)}
        >
          {column.label}
        </TH>
      ))}
    </tr>
  </THead>
);

/**
 * The store-user edit form is shared with the Edit tab's "Edit Store
 * Manager/Owner" card, so its state and handlers live in PlatformAdmin.
 */
export interface EditUserControls {
  open: boolean;
  setOpen: (open: boolean) => void;
  setId: (id: number | null) => void;
  load: (id: number) => void;
  form: Record<string, any> | null;
  error: string;
  success: string;
  setError: (message: string) => void;
  setSuccess: (message: string) => void;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => void;
  onSubmit: (e: React.FormEvent) => void;
}

export interface UsersTabProps {
  storeUsers: StoreUser[];
  loadingStoreUsers: boolean;
  storeGroupUsers: StoreGroupUser[];
  loadingStoreGroupUsers: boolean;
  stores: { id: number; name: string }[];
  storeOptions: SelectOption[];
  loadingStores: boolean;
  storeUserOptions: SelectOption[];
  storeGroupOptions: SelectOption[];
  loadingStoreGroups: boolean;
  page: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onStoreUsersChanged: () => void;
  onStoreGroupUsersChanged: () => void;
  /** Also written by the onboarding intake prefill. */
  userForm: UserFormData;
  setUserForm: React.Dispatch<React.SetStateAction<UserFormData>>;
  /** Also written by the onboarding intake prefill. */
  userSuccess: string;
  setUserSuccess: (message: string) => void;
  onPopulateFromIntake: () => void;
  intakePicker: IntakePickerControls;
  editUser: EditUserControls;
  search?: string;
  onSearchChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  sort?: TableSortState<string>;
  onSort?: (key: string) => void;
  filtering?: boolean;
}

const UsersTab: React.FC<UsersTabProps> = ({
  storeUsers,
  loadingStoreUsers,
  storeGroupUsers,
  loadingStoreGroupUsers,
  stores,
  storeOptions,
  loadingStores,
  storeUserOptions,
  storeGroupOptions,
  loadingStoreGroups,
  page,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  onStoreUsersChanged,
  onStoreGroupUsersChanged,
  userForm,
  setUserForm,
  userSuccess,
  setUserSuccess,
  onPopulateFromIntake,
  intakePicker,
  editUser,
  search = '',
  onSearchChange,
  sort = { key: null, direction: 'asc' },
  onSort,
  filtering = false,
}) => {
  const { accessToken } = useAuth();

  const [groupSearch, setGroupSearch] = useState('');
  const [groupSort, setGroupSort] = useState<TableSortState<GroupUserSortKey>>({
    key: null,
    direction: 'asc',
  });
  const visibleGroupUsers = useMemo(
    () =>
      filterAndSortRows(
        storeGroupUsers,
        groupSearch,
        (membership) =>
          rowSearchText([
            membership.id,
            membership.user_email,
            membership.user,
            membership.store_group_name,
            membership.store_group,
            storeGroupRoleLabel(membership.role),
            membership.role,
          ]),
        groupSort,
        (membership, key) => {
          if (key === 'user') return membership.user_email || membership.user;
          if (key === 'store_group') {
            return membership.store_group_name || membership.store_group;
          }
          if (key === 'role') return storeGroupRoleLabel(membership.role);
          return membership.id;
        },
      ),
    [groupSearch, groupSort, storeGroupUsers],
  );
  const filteringGroups = groupSearch.trim().length > 0;

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [assignmentType, setAssignmentType] =
    useState<UserAssignmentType>('store');
  const [groupMode, setGroupMode] = useState<StoreGroupUserMode>('new');
  const [groupForm, setGroupForm] = useState<StoreGroupUserFormData>(
    EMPTY_STORE_GROUP_USER_FORM,
  );
  const [userError, setUserError] = useState('');

  const [isEditGroupUserOpen, setIsEditGroupUserOpen] = useState(false);
  const [editGroupUserId, setEditGroupUserId] = useState<number | null>(null);
  const [editGroupUserForm, setEditGroupUserForm] =
    useState<StoreGroupUserFormData>(EMPTY_STORE_GROUP_USER_FORM);
  const [editGroupUserError, setEditGroupUserError] = useState('');
  const [editGroupUserSuccess, setEditGroupUserSuccess] = useState('');

  const clearCreateMessages = () => {
    setUserError('');
    setUserSuccess('');
  };

  const openCreate = () => {
    setIsCreateOpen(true);
    clearCreateMessages();
  };

  const closeCreate = () => {
    setIsCreateOpen(false);
    clearCreateMessages();
  };

  const handleUserChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    setUserForm(updateUserForm(userForm, name, value, type));
  };

  const handleUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUserError('');
    setUserSuccess('');

    if (!accessToken) {
      setUserError('You must be logged in to create a user');
      return;
    }

    if (assignmentType === 'store_group') {
      const invalid = validateStoreGroupUserForm(groupForm, groupMode);
      if (invalid) {
        setUserError(invalid);
        return;
      }

      try {
        const response = await api.post(
          apiEndpoints.createStoreGroupUser(),
          storeGroupUserPayload(groupForm, groupMode),
        );
        if (response && response.ok) {
          const data = await response.json();
          setUserSuccess(storeGroupUserCreatedMessage(data));
          setGroupForm(EMPTY_STORE_GROUP_USER_FORM);
          onStoreGroupUsersChanged();
        } else if (response) {
          const data = await response.json().catch(() => ({}));
          setUserError(
            responseError(data, 'Failed to create store group user'),
          );
        } else {
          setUserError('Failed to create store group user');
        }
      } catch {
        setUserError('Failed to create store group user');
      }
      return;
    }

    if (!userForm.store_id) {
      setUserError('Please select a store');
      return;
    }

    try {
      const response = await api.post(apiEndpoints.createStoreUser(), userForm);

      if (response && response.ok) {
        const data = await response.json();
        console.log('Store user created successfully:', data);
        setUserSuccess(storeUserCreatedMessage(data.email, userForm.role));
        setUserForm(EMPTY_USER_FORM);
      } else if (response) {
        const data = await response.json();
        setUserError(responseError(data, 'Failed to create user'));
      } else {
        setUserError('Failed to create user');
      }
    } catch (err: any) {
      console.error('User creation error:', err);
      setUserError(thrownErrorMessage(err));
    }
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    await handleUserSubmit(e);
    // Reads the error from before this submit, as the legacy form did.
    if (!userError) {
      onStoreUsersChanged();
    }
  };

  const openEditUser = (user: StoreUser) => {
    editUser.setId(user.id);
    editUser.load(user.id);
    editUser.setOpen(true);
  };

  const closeEditUser = () => {
    editUser.setOpen(false);
    editUser.setError('');
    editUser.setSuccess('');
  };

  const openEditGroupUser = (membership: StoreGroupUser) => {
    setEditGroupUserId(membership.id);
    setEditGroupUserForm(storeGroupUserFormFrom(membership));
    setEditGroupUserError('');
    setEditGroupUserSuccess('');
    setIsEditGroupUserOpen(true);
  };

  const closeEditGroupUser = () => {
    setIsEditGroupUserOpen(false);
    setEditGroupUserError('');
    setEditGroupUserSuccess('');
  };

  const handleEditGroupUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editGroupUserId) return;
    setEditGroupUserError('');
    setEditGroupUserSuccess('');

    const invalid = validateEditStoreGroupUserForm(editGroupUserForm);
    if (invalid) {
      setEditGroupUserError(invalid);
      return;
    }

    try {
      const response = await api.patch(
        apiEndpoints.updateStoreGroupUser(editGroupUserId),
        editGroupUserForm,
      );
      if (response && response.ok) {
        setEditGroupUserSuccess('Store group user updated.');
        onStoreGroupUsersChanged();
        setTimeout(() => setIsEditGroupUserOpen(false), 800);
      } else if (response) {
        const data = await response.json().catch(() => ({}));
        setEditGroupUserError(
          responseError(data, 'Failed to update store group user'),
        );
      } else {
        setEditGroupUserError('Failed to update store group user');
      }
    } catch {
      setEditGroupUserError('Failed to update store group user');
    }
  };

  return (
    <>
      <ListPage
        title="Users"
        toolbar={
          <div className="flex min-h-11 items-center px-4 py-2 lg:px-6">
            <TableSearch
              label="Search store users"
              placeholder="Search name, email, role, or store…"
              value={search}
              onChange={onSearchChange ?? (() => undefined)}
            />
          </div>
        }
        actions={
          <Button
            variant="primary"
            leading={<IconPlus size={14} />}
            onClick={openCreate}
          >
            Create
          </Button>
        }
      >
        {!loadingStoreUsers && storeUsers.length === 0 ? (
          <EmptyState
            icon={<IconUsers />}
            title={
              filtering
                ? 'No users match that search'
                : "You don't have any users at the moment"
            }
            description={
              filtering
                ? 'Try a name, email, role, or store.'
                : "When users appear, they'll show up here."
            }
          />
        ) : (
          <>
            {loadingStoreUsers && (
              <p role="status" className="sr-only">
                Loading users…
              </p>
            )}
            <div className="v2-scroll overflow-x-auto">
              <Table fixed aria-label="Store users" className="min-w-[900px]">
                <Header
                  columns={STORE_USER_COLUMNS}
                  sort={sort}
                  onSort={onSort}
                  loading={loadingStoreUsers}
                />
                <TBody>
                  {loadingStoreUsers ? (
                    <TableSkeletonRows cols={STORE_USER_COLUMNS.length} />
                  ) : (
                    storeUsers.map((user) => (
                      <TR key={user.id}>
                        <TD num className="font-medium">
                          {user.id}
                        </TD>
                        <TD truncate className="font-medium">
                          {storeUserName(user)}
                        </TD>
                        <TD truncate>
                          <Placeholder value={user.email} />
                        </TD>
                        <TD>
                          {user.role ? (
                            <Tag tone={storeUserRoleTone(user.role)}>
                              {user.role}
                            </Tag>
                          ) : (
                            <Placeholder />
                          )}
                        </TD>
                        <TD truncate>{storeUserStoreLabel(user, stores)}</TD>
                        <TD>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="-ml-2.5"
                            onClick={() => openEditUser(user)}
                          >
                            Edit User
                          </Button>
                        </TD>
                      </TR>
                    ))
                  )}
                </TBody>
              </Table>
            </div>
          </>
        )}
        {totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPage={(next) =>
              onPageChange(Math.min(Math.max(next, 1), totalPages))
            }
            noun="users"
          />
        )}

        <div className="border-t border-v2-line px-4 pb-1 pt-4 lg:px-6">
          <SectionTitleRow title="Store Group Users" />
        </div>
        <div className="flex min-h-11 items-center px-4 py-2 lg:px-6">
          <TableSearch
            label="Search store group users"
            placeholder="Search user, store group, or role…"
            value={groupSearch}
            onChange={(event) => setGroupSearch(event.target.value)}
          />
        </div>
        {!loadingStoreGroupUsers && visibleGroupUsers.length === 0 ? (
          <EmptyState
            compact
            icon={<IconUsers />}
            title={
              filteringGroups
                ? 'No store group users match that search'
                : "You don't have any store group users at the moment"
            }
            description={
              filteringGroups
                ? 'Try a user, store group, or role.'
                : "When store group users appear, they'll show up here."
            }
          />
        ) : (
          <>
            {loadingStoreGroupUsers && (
              <p role="status" className="sr-only">
                Loading store group users…
              </p>
            )}
            <div className="v2-scroll overflow-x-auto">
              <Table
                fixed
                aria-label="Store group users"
                className="min-w-[900px]"
              >
                <Header
                  columns={STORE_GROUP_USER_COLUMNS}
                  sort={groupSort}
                  onSort={(key) =>
                    setGroupSort((current) =>
                      toggleSortState(current, key as GroupUserSortKey),
                    )
                  }
                  loading={loadingStoreGroupUsers}
                />
                <TBody>
                  {loadingStoreGroupUsers ? (
                    <TableSkeletonRows
                      rows={4}
                      cols={STORE_GROUP_USER_COLUMNS.length}
                    />
                  ) : (
                    visibleGroupUsers.map((membership) => (
                      <TR key={membership.id}>
                        <TD num className="font-medium">
                          {membership.id}
                        </TD>
                        <TD truncate className="font-medium">
                          {membership.user_email || membership.user}
                        </TD>
                        <TD truncate>
                          {membership.store_group_name ||
                            membership.store_group}
                        </TD>
                        <TD>
                          <Tag>{storeGroupRoleLabel(membership.role)}</Tag>
                        </TD>
                        <TD>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="-ml-2.5"
                            onClick={() => openEditGroupUser(membership)}
                          >
                            Edit Group User
                          </Button>
                        </TD>
                      </TR>
                    ))
                  )}
                </TBody>
              </Table>
            </div>
          </>
        )}
      </ListPage>

      <EditUserDialog
        open={editUser.open}
        onClose={closeEditUser}
        onSubmit={editUser.onSubmit}
        form={editUser.form}
        error={editUser.error}
        success={editUser.success}
        onChange={editUser.onChange}
        storeOptions={storeOptions}
      />

      <EditStoreGroupUserDialog
        open={isEditGroupUserOpen}
        onClose={closeEditGroupUser}
        onSubmit={handleEditGroupUserSubmit}
        form={editGroupUserForm}
        onChange={setEditGroupUserForm}
        error={editGroupUserError}
        success={editGroupUserSuccess}
        storeUserOptions={storeUserOptions}
        loadingStoreUsers={loadingStoreUsers}
        storeGroupOptions={storeGroupOptions}
        loadingStoreGroups={loadingStoreGroups}
      />

      <CreateUserDialog
        open={isCreateOpen}
        onClose={closeCreate}
        onSubmit={handleCreateSubmit}
        error={userError}
        success={userSuccess}
        assignmentType={assignmentType}
        onAssignmentTypeChange={(type) => {
          if (type === assignmentType) return;
          setAssignmentType(type);
          clearCreateMessages();
        }}
        userForm={userForm}
        onUserChange={handleUserChange}
        onPopulateFromIntake={onPopulateFromIntake}
        storeOptions={storeOptions}
        loadingStores={loadingStores}
        groupMode={groupMode}
        onGroupModeChange={(mode) => {
          if (mode === groupMode) return;
          setGroupMode(mode);
          clearCreateMessages();
        }}
        groupForm={groupForm}
        onGroupFormChange={setGroupForm}
        storeUserOptions={storeUserOptions}
        loadingStoreUsers={loadingStoreUsers}
        storeGroupOptions={storeGroupOptions}
        loadingStoreGroups={loadingStoreGroups}
      />

      <IntakePrefillDialog picker={intakePicker} />
    </>
  );
};

export default UsersTab;
